import { z } from "zod";
import {
  Box,
  WidgetEmpty,
  WidgetSkeleton,
  XYChart,
  BarSeries,
  Axis,
  ChartGrid,
  ChartTooltip,
  angledTickLabelProps,
  compactNumber,
  useWidgetInstance,
  type WidgetModule,
  type WidgetRenderContext,
  type WidgetSettingsGroup,
  truncateLabel,
} from "@salestrak/ui";
import type { WidgetTag } from "#app/widgets/dashboards/widget-tags";
import { WIDGET_DIMENSIONS, WIDGET_DIMENSION_OPTIONS } from "#app/widgets/dashboards/use-dimension-counts";
import { widgetFiltersSchema } from "#app/widgets/dashboards/widget-filters";
import { useWidgetDrilldown } from "#app/widgets/dashboards/widget-drilldown";
import {
  useShapedSlices,
  sliceDrilldownIds,
  SLICE_SHOW_OPTIONS,
  SLICE_SORT_OPTIONS,
  type DimensionSlice,
} from "#app/widgets/dashboards/use-dimension-slices";
import { WidgetHeaderFilters } from "../shared/widget-header-filters";
import { WidgetChartTooltip } from "../shared/widget-chart-tooltip";
import { workloadTitle } from "../shared/derived-titles";

/**
 * `dimension-bar` — a themed single-series bar chart of projects grouped by any
 * dimension (stage / type / team / assignee / age), on the `XYChart` scaffold.
 * Each bar wears its entity's OWN color (`sliceColors` — stage/team/type icon
 * color, ramp fallback; `colorAccessor` so the per-bar color wins). Reads the
 * shared `useShapedSlices` plumbing and the same Show / Sort vocabulary as the
 * pie and battery cards; the tail folds into an "Other" bar. Serves the Custom
 * "Bar chart" plus the by-stage / by-assignee "Bar" presets.
 */
export const dimensionBarConfigSchema = z
  .looseObject({
    measure: z.enum(["count"]).default("count"),
    groupBy: z.enum(WIDGET_DIMENSIONS).default("stage"),
    show: z.enum(["top5", "top8", "top12", "all"]).default("top8"),
    sort: z.enum(["volume", "alpha"]).default("volume"),
    showGrid: z.boolean().default(true),
    showAxes: z.boolean().default(true),
    dateField: z.enum(["creationDate", "lastUpdateDate"]).optional(),
    filters: widgetFiltersSchema.nullish(),
  });

export type DimensionBarConfig = z.infer<typeof dimensionBarConfigSchema>;

const xAccessor = (d: DimensionSlice) => d.label;
const yAccessor = (d: DimensionSlice) => d.total;

function DimensionBarRender({ h, config }: WidgetRenderContext<DimensionBarConfig>) {
  // Older persisted Team Overview cards predate `dateField`; keep those cards on
  // the activity-oriented update window without forcing a layout migration.
  const dateField =
    config.dateField === "lastUpdateDate" || config.widgetKey === "team-overview.workload"
      ? "lastUpdateDate"
      : "creationDate";
  const { data, slices: rows, sliceColors, loading, error } = useShapedSlices({
    groupBy: config.groupBy,
    sort: config.sort,
    show: config.show,
    filters: config.filters,
    dateField,
  });
  const total = rows.reduce((sum, row) => sum + row.total, 0);
  // Click-through: a bar press opens this card's Data drawer on the bar's rows.
  const drill = useWidgetDrilldown();
  const instance = useWidgetInstance();

  if (error) throw error;
  if (loading) {
    return <WidgetSkeleton variant="rows" />;
  }
  if (rows.length === 0) {
    return <WidgetEmpty icon="bar-chart-3" title="No projects in range" />;
  }

  const compact = h < 3;
  const showAxes = config.showAxes && !compact;
  const showGrid = config.showGrid && !compact;

  return (
    <Box direction="column" fill="flex" gap="xs" cursor={drill && instance ? "pointer" : undefined}>
      <XYChart<DimensionSlice>
        xScale={{ type: "band", paddingInner: 0.3, paddingOuter: 0.2 }}
        yScale={{ type: "linear", nice: true }}
        margin={{ top: 8, right: 8, bottom: showAxes ? 52 : 8, left: showAxes ? 32 : 8 }}
        ariaLabel="Projects by group"
        onPointerUp={
          drill && instance
            ? ({ datum }) => {
                if (datum)
                  drill.openData(instance.instanceId, {
                    kind: "dimension",
                    dimension: config.groupBy,
                    ids: sliceDrilldownIds(datum, data, rows),
                    label: datum.label,
                  });
              }
            : undefined
        }
      >
        {showGrid ? <ChartGrid rows columns={false} numTicks={4} /> : null}
        {showAxes ? <Axis orientation="left" numTicks={4} tickFormat={compactNumber} /> : null}
        {showAxes ? (
          <Axis
            orientation="bottom"
            tickFormat={(v) => truncateLabel(String(v))}
            tickLabelProps={angledTickLabelProps}
          />
        ) : null}
        <BarSeries
          dataKey="count"
          data={rows}
          xAccessor={xAccessor}
          yAccessor={yAccessor}
          colorAccessor={(d) => sliceColors.scale(d.label)}
        />
        <ChartTooltip
          snapTooltipToDatumX
          showVerticalCrosshair
          renderTooltip={({ tooltipData }) => {
            const d = tooltipData?.nearestDatum?.datum as DimensionSlice | undefined;
            if (!d) return null;
            return (
              <WidgetChartTooltip
                label={d.label}
                details={`${d.total} ${d.total === 1 ? "project" : "projects"} · ${total > 0 ? Math.round((d.total / total) * 100) : 0}%`}
              />
            );
          }}
        />
      </XYChart>
    </Box>
  );
}

const dimensionBarSettings: WidgetSettingsGroup[] = [
  {
    title: "Source",
    fields: [{ key: "source", kind: "source", label: "Workflow" }],
  },
  {
    title: "Data",
    fields: [
      {
        key: "groupBy",
        kind: "select",
        label: "Group by",
        icon: "layers",
        options: WIDGET_DIMENSION_OPTIONS,
      },
      { key: "show", kind: "select", label: "Show", icon: "eye", options: SLICE_SHOW_OPTIONS },
      {
        key: "sort",
        kind: "select",
        label: "Sort by",
        icon: "arrow-down-wide-narrow",
        options: SLICE_SORT_OPTIONS,
      },
    ],
  },
  {
    title: "Display",
    fields: [
      { key: "showGrid", kind: "toggle", label: "Gridlines", icon: "grid-dots" },
      { key: "showAxes", kind: "toggle", label: "Axes", icon: "ruler" },
    ],
  },
  {
    title: "Filters",
    fields: [{ key: "filters", kind: "issueFilters", label: "Filters" }],
  },
];

export const dimensionBarDef: WidgetModule<DimensionBarConfig> = {
  type: "dimension-bar",
  title: "Bar chart",
  dynamicTitle: (config) => workloadTitle(config.title, config.groupBy),
  icon: "bar-chart-3",
  span: { minW: 3, minH: 2, maxW: 12, maxH: 8, defaultW: 5, defaultH: 4 },
  configSchema: dimensionBarConfigSchema,
  settings: dimensionBarSettings,
  headerActions: WidgetHeaderFilters,
  // Chart body: grab the card from anywhere on it — wedge/bar/point clicks
  // still land because drag activation needs 4px of movement.
  bodyDraggable: true,
  catalog: [
    {
      key: "dimension-bar.custom",
      label: "Bar chart",
      description: "Compare project counts by type, stage, team, assignee, or health.",
      section: "Custom",
      defaultConfig: { groupBy: "stage" },
      defaultW: 5,
      defaultH: 4,
    },
    {
      key: "dimension-bar.stage",
      label: "Workload by stage",
      description: "Project counts in each pipeline stage, as bars.",
      section: "Charts",
      tags: ["projects", "stages"] satisfies readonly WidgetTag[],
      defaultConfig: { groupBy: "stage" },
      defaultW: 5,
      defaultH: 4,
      // The label matches the derived title, so stay untitled and let
      // `dynamicTitle` track `groupBy` as the user regroups.
      autoTitle: false,
    },
    {
      key: "dimension-bar.assignee",
      label: "Workload by assignee",
      description: "Project counts by assignee, as bars.",
      section: "Charts",
      tags: ["projects", "members"] satisfies readonly WidgetTag[],
      defaultConfig: { groupBy: "assignee" },
      defaultW: 5,
      defaultH: 4,
      autoTitle: false,
    },
    {
      key: "dimension-bar.leaderboard",
      label: "Leaderboard",
      description: "Rank members by project volume and turn ownership into a competition.",
      section: "Charts",
      tags: ["projects", "members"] satisfies readonly WidgetTag[],
      defaultConfig: { groupBy: "assignee", sort: "volume", show: "top12" },
      defaultW: 5,
      defaultH: 4,
    },
  ],
  render: DimensionBarRender,
};
